import React, { useState } from "react";
import { useAIAgents } from "@/hooks/useAIAgents";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Play,
  Pause,
  XCircle,
  BarChart3,
  Calendar,
  Users,
  Zap,
  Settings,
  ArrowRight,
  GraduationCap,
} from "lucide-react";
import { AIAgentWithStats } from "@/lib/services/ai-agents";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { usePlansWithFeatures } from "@/hooks/use-plans-data";
import PlanSelectionModal from "../modals/PlanSelectionModal";
import { cn } from "@/lib/utils";

const agentIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  "contract-review": Activity,
  "campus-consultorio": GraduationCap,
  "legal-research": BarChart3,
  "compliance-monitor": Settings,
  "legal-chatbot": Users,
  "litigation-predictor": TrendingUp,
  "ip-assistant": Zap,
  "document-generator": Calendar,
  "compliance-ethics": AlertTriangle,
  "debt-collection": Clock,
  "consumer-assistant": CheckCircle,
};

const agentGradients: Record<string, string> = {
  "contract-review": "from-red-500 to-rose-600",
  "campus-consultorio": "from-indigo-600 to-emerald-600",
  "legal-research": "from-blue-500 to-indigo-600",
  "compliance-monitor": "from-orange-500 to-red-600",
  "legal-chatbot": "from-purple-500 to-violet-600",
  "litigation-predictor": "from-teal-500 to-cyan-600",
  "ip-assistant": "from-amber-500 to-yellow-600",
  "document-generator": "from-emerald-500 to-teal-600",
  "compliance-ethics": "from-rose-500 to-pink-600",
  "debt-collection": "from-slate-500 to-gray-600",
  "consumer-assistant": "from-green-500 to-emerald-600",
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case "active":
      return {
        icon: <CheckCircle className="h-3.5 w-3.5" />,
        label: "Activo",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    case "maintenance":
      return {
        icon: <Settings className="h-3.5 w-3.5" />,
        label: "Mantenimiento",
        className: "bg-amber-50 text-amber-700 border-amber-200",
      };
    case "disabled":
      return {
        icon: <XCircle className="h-3.5 w-3.5" />,
        label: "Deshabilitado",
        className: "bg-red-50 text-red-700 border-red-200",
      };
    default:
      return {
        icon: <Minus className="h-3.5 w-3.5" />,
        label: "Desconocido",
        className: "bg-gray-50 text-gray-700 border-gray-200",
      };
  }
};

const formatLastUsed = (lastUsed: string | null) => {
  if (!lastUsed) return "Nunca usado";
  const date = new Date(lastUsed);
  const now = new Date();
  const diffInHours = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60)
  );
  if (diffInHours < 1) return "Hace menos de 1 hora";
  if (diffInHours < 24) return `Hace ${diffInHours} horas`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `Hace ${diffInDays} días`;
  return date.toLocaleDateString();
};

export function EnhancedAIAgentsSection() {
  const { agents, loading, error, trackUsage } = useAIAgents();
  const { user } = useAuth();
  const navigate = useNavigate();
  const trialStatus = useTrialStatus();
  const { data: plans } = usePlansWithFeatures();
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState("all");

  const activeAgents = agents.filter(
    (agent) => agent.maintenance_status === "active"
  );
  const maintenanceAgents = agents.filter(
    (agent) => agent.maintenance_status === "maintenance"
  );
  const disabledAgents = agents.filter(
    (agent) => agent.maintenance_status === "disabled"
  );

  const isEmailConfirmed = user && user.email_confirmed_at !== null;
  const hasNoAgents = user && agents.length === 0 && !loading;
  const shouldShowEmailConfirmation =
    user && (!isEmailConfirmed || hasNoAgents);

  const handleUseAgent = async (agentId: string) => {
    await trackUsage(agentId, { action: "accessed_from_dashboard" });
    navigate(`/dashboard/agents/${agentId}`);
  };

  const renderAgentCard = (agent: AIAgentWithStats, index: number) => {
    const Icon = agentIcons[agent.id] || Activity;
    const gradient = agentGradients[agent.id] || "from-gray-500 to-gray-600";
    const statusConfig = getStatusConfig(agent.maintenance_status);
    const usagePercentage = agent.daily_usage_limit
      ? (agent.current_daily_usage / agent.daily_usage_limit) * 100
      : 0;

    return (
      <Card
        key={agent.id}
        className={`relative border border-gray-100/80 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-up stagger-${(index % 11) + 1}`}>
        {agent.maintenance_status !== "active" && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                {agent.maintenance_status === "maintenance" ? (
                  <Settings className="h-6 w-6 text-amber-500" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-400" />
                )}
              </div>
              <p className="text-sm font-medium text-gray-600">
                {agent.maintenance_status === "maintenance"
                  ? "En Mantenimiento"
                  : "Deshabilitado"}
              </p>
              {agent.maintenance_message && (
                <p className="text-xs text-gray-400 mt-1 max-w-xs">
                  {agent.maintenance_message}
                </p>
              )}
            </div>
          </div>
        )}

        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{agent.name}</CardTitle>
                <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{agent.description}</p>
              </div>
            </div>
            <Badge className={`${statusConfig.className} text-[10px] font-medium border flex items-center gap-1`}>
              {statusConfig.icon}
              <span>{statusConfig.label}</span>
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 pt-0">
          {/* Usage Statistics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-gray-50 rounded-xl">
              <div className="text-xl font-bold text-gray-900">
                {agent.usage_count}
              </div>
              <div className="text-[11px] text-gray-400">Total usos</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-xl">
              <div className="text-xl font-bold text-gray-900">
                {agent.current_daily_usage || 0}
              </div>
              <div className="text-[11px] text-gray-400">Hoy</div>
            </div>
          </div>

          {/* Daily Usage Progress */}
          {agent.daily_usage_limit && (
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-gray-500">Uso diario</span>
                <span className="font-medium text-gray-700">
                  {agent.current_daily_usage || 0} / {agent.daily_usage_limit}
                </span>
              </div>
              <Progress value={usagePercentage} className="h-1.5" />
            </div>
          )}

          {/* Last Used */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Último uso:</span>
            <span className="font-medium text-gray-600">
              {formatLastUsed(agent.last_used)}
            </span>
          </div>

          {/* Action Button */}
          <Button
            onClick={() => handleUseAgent(agent.id)}
            disabled={agent.maintenance_status !== "active"}
            className={`w-full rounded-xl h-10 text-sm font-medium transition-all duration-300 ${
              agent.maintenance_status === "active"
                ? "btn-gradient hover:scale-[1.02] shadow-md shadow-legal-blue/15"
                : ""
            }`}
            variant={
              agent.maintenance_status === "active" ? "default" : "outline"
            }>
            {agent.maintenance_status === "active" ? (
              <>
                <Play className="mr-2 h-4 w-4" />
                Usar Ahora
              </>
            ) : (
              <>
                <Pause className="mr-2 h-4 w-4" />
                No Disponible
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-6 space-y-5">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-gray-100">
              <CardHeader>
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-20 w-full rounded-xl" />
                <Skeleton className="h-10 w-full rounded-xl" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 sm:p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (shouldShowEmailConfirmation) {
    return (
      <div className="p-3 sm:p-6">
        <div className="text-center py-16">
          <div className="mx-auto w-20 h-20 bg-amber-50 rounded-2xl flex items-center justify-center mb-6">
            <svg
              className="w-10 h-10 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Acceso a agentes IA no disponible
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto text-sm">
            Para acceder a los agentes de IA, necesitas completar la
            verificación de tu cuenta.
          </p>
          <div className="flex flex-col gap-3 justify-center sm:flex-row">
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              className="rounded-xl">
              Verificar de nuevo
            </Button>
            <Button
              onClick={() => navigate("/pricing")}
              className="btn-gradient rounded-xl">
              Verificar suscripción
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            ¿No recibiste el correo?{" "}
            <button
              onClick={() => navigate("/forgot-password")}
              className="text-legal-blue hover:underline">
              Solicita un nuevo enlace
            </button>
          </p>
        </div>
      </div>
    );
  }

  const summaryStats = [
    {
      label: "Total Agentes",
      value: agents.length,
      icon: Activity,
      gradient: "from-blue-500 to-indigo-600",
      sub: "Agentes disponibles",
    },
    {
      label: "Activos",
      value: activeAgents.length,
      icon: CheckCircle,
      gradient: "from-emerald-500 to-teal-600",
      sub: "Listos para usar",
    },
    {
      label: "Mantenimiento",
      value: maintenanceAgents.length,
      icon: Settings,
      gradient: "from-amber-500 to-orange-600",
      sub: "En actualización",
    },
    {
      label: "Deshabilitados",
      value: disabledAgents.length,
      icon: XCircle,
      gradient: "from-red-500 to-rose-600",
      sub: "Temporalmente no disponibles",
    },
  ];

  return (
    <div className="p-6 space-y-6 relative min-h-[600px]">
      {/* Absolute Overlay for Expired State */}
      {trialStatus.isExpired && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-gray-50/10 backdrop-blur-[4px] rounded-2xl animate-fade-in pointer-events-auto">
          <div className="bg-white p-10 rounded-[32px] shadow-2xl border border-gray-100 text-center max-w-md mx-4 transform transition-all hover:scale-[1.01] ring-1 ring-black/5">
            <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-inner">
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">Acceso Restringido</h3>
            <p className="text-base text-gray-500 mb-10 leading-relaxed">
              Tu periodo de prueba ha finalizado. Para acceder a todas nuestras funciones y agentes de IA legal, por favor actualiza tu plan.
            </p>
            <Button 
              onClick={() => setShowPlansModal(true)}
              className="w-full bg-legal-blue hover:bg-legal-navy text-white h-14 rounded-2xl font-bold text-lg shadow-xl shadow-blue-200 transition-all hover:shadow-blue-300 active:scale-[0.98]"
            >
              Actualizar Plan Ahora
            </Button>
          </div>
        </div>
      )}

      <PlanSelectionModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        plans={plans || []}
      />

      <div className={cn("space-y-6", trialStatus.isExpired && "opacity-40 pointer-events-none blur-[2px]")}>
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {summaryStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className={`border border-gray-100/80 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 animate-fade-in-up stagger-${index + 1}`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-medium text-gray-500">{stat.label}</CardTitle>
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-md`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  <p className="text-xs text-gray-400 mt-0.5">{stat.sub}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="bg-gray-100 p-1 rounded-xl h-auto">
            <TabsTrigger value="all" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Todos ({agents.length})
            </TabsTrigger>
            <TabsTrigger value="active" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Activos ({activeAgents.length})
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Mantenimiento ({maintenanceAgents.length})
            </TabsTrigger>
            <TabsTrigger value="disabled" className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Deshabilitados ({disabledAgents.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-5">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {agents.map((agent, i) => renderAgentCard(agent, i))}
            </div>
          </TabsContent>

          <TabsContent value="active" className="mt-5">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {activeAgents.map((agent, i) => renderAgentCard(agent, i))}
            </div>
          </TabsContent>

          <TabsContent value="maintenance" className="mt-5">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {maintenanceAgents.map((agent, i) => renderAgentCard(agent, i))}
            </div>
          </TabsContent>

          <TabsContent value="disabled" className="mt-5">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {disabledAgents.map((agent, i) => renderAgentCard(agent, i))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
